import type { User } from '~/types'

export const useAuth = () => {
  const user = useState<User | null>('auth:user', () => null)
  const loading = useState<boolean>('auth:loading', () => false)

  const fetchUser = async () => {
    loading.value = true
    try {
      const { user: fetchedUser } = await $fetch('/api/auth/me', {
        credentials: 'include'
      })
      user.value = fetchedUser
      return fetchedUser
    } catch (error: any) {
      // Solo cancella l'utente se è un errore 401 (non autenticato)
      // Altri errori potrebbero essere temporanei (rete, server down, etc)
      if (error?.statusCode === 401 || error?.status === 401) {
        user.value = null
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const login = async (email: string, password: string) => {
    loading.value = true
    try {
      const { user: loggedUser } = await $fetch('/api/auth/login', {
        method: 'POST',
        body: { email, password },
        credentials: 'include'
      })
      user.value = loggedUser
      return { success: true, user: loggedUser }
    } catch (error: any) {
      return {
        success: false,
        error: error?.data?.message || error?.message || 'Errore durante il login'
      }
    } finally {
      loading.value = false
    }
  }

  const register = async (data: {
    email: string
    password: string
    firstName?: string
    lastName?: string
    role?: 'admin' | 'athlete'
  }) => {
    loading.value = true
    try {
      const { user: registeredUser } = await $fetch('/api/auth/register', {
        method: 'POST',
        body: data,
        credentials: 'include'
      })
      user.value = registeredUser
      return { success: true, user: registeredUser }
    } catch (error: any) {
      return {
        success: false,
        error: error?.data?.message || error?.message || 'Errore durante la registrazione'
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    try {
      await $fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })
      user.value = null
      // Redirect to home or login page
      await navigateTo('/')
      return { success: true }
    } catch (error: any) {
      return {
        success: false,
        error: error?.data?.message || error?.message || 'Errore durante il logout'
      }
    } finally {
      loading.value = false
    }
  }

  const isAuthenticated = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isAthlete = computed(() => user.value?.role === 'athlete')

  return {
    user: readonly(user),
    loading: readonly(loading),
    isAuthenticated,
    isAdmin,
    isAthlete,
    fetchUser,
    login,
    register,
    logout
  }
}
